import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, unref as _unref, normalizeClass as _normalizeClass, renderSlot as _renderSlot, resolveComponent as _resolveComponent, withModifiers as _withModifiers, createVNode as _createVNode, mergeProps as _mergeProps, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-6571ea22"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "ticket-card__body ticket-card__items" }
const _hoisted_2 = /*#__PURE__*/ _withScopeId(() => /*#__PURE__*/_createElementVNode("hr", null, null, -1))
const _hoisted_3 = { class: "ticket-card__footer" }
const _hoisted_4 = { class: "ticket-card__total" }
const _hoisted_5 = { class: "ticket-card__total-price" }
const _hoisted_6 = {
  key: 0,
  class: "ticket-card__label"
}
const _hoisted_7 = { class: "ticket-card__prices-wrapper" }
const _hoisted_8 = {
  key: 0,
  class: "ticket-card__price ticket-card__price--base ticket-card__price--discounted"
}
const _hoisted_9 = { key: 0 }
const _hoisted_10 = { class: "ticket-card__action" }
const _hoisted_11 = {
  key: 1,
  class: "ticket-card__alerts"
}

import { computed, PropType, ref, watch } from 'vue'
import { formatCurrency, formatServerDate } from '@/utils/formatters'
import { ALERT_TYPES, FORMATS, SIZES } from '@/enums/components'
import { getValidationErrorData } from '@/utils/errors'
import { Alert } from '@/models/shared.model'
import {
  useCreateOrderMutation,
  createTicketItemOrderItems,
} from '@/queries/cart'
import { ICreateTicketServiceOrderItem } from '@/models/public/order.model'
import { useOrderStore } from '@/store/order'
import { useToast } from 'vue-toastification'
import { useI18n } from 'vue-i18n'
import { serviceResultCardCommonProps } from '@/enums/public'
import { isNumeric } from '@/utils/general'
import { debounce } from 'lodash'
import {
  useFetchTicketItemPriceInformationMutation,
  useFetchTicketItemDateAvailabilityListMutation,
} from '@/queries/service/serviceTicket'
import {
  IServiceTicketItemId,
  ITicketItemDateAvailability,
  ITicketServiceItemPriceInformationBodyItem,
  ITicketServicePriceInformation,
} from '@/models/service/serviceTicket.model'
import TicketResultCardSearch from '@/components/public/ticket/TicketResultCardSearch.vue'
import TicketResultCardItem from '@/components/public/ticket/TicketResultCardItem.vue'


export default /*#__PURE__*/_defineComponent({
  __name: 'TicketResultCard',
  props: {
  ...serviceResultCardCommonProps,
  selectedGroupSize: {
    type: Number as PropType<number | undefined>,
    default: undefined,
  },
},
  emits: ['cartUpdated', 'payloadUpdated'],
  setup(__props, { emit: emits }) {

const props = __props

const orderStore = useOrderStore()
const i18n = useI18n()
const toast = useToast()



const isLoadedOnce = ref<boolean>(false)
const isAddToCartLoading = ref<boolean>(false)
const errors = ref<Record<string, unknown> | undefined>(undefined)
const selectedDate = ref<Date | null>(null)
const priceInfo = ref<ITicketServicePriceInformation | null>(null)
const availableTicketItems = ref<ITicketItemDateAvailability[] | null>(null)
const ticketItemSelectionsRecords = ref<
  Record<
    IServiceTicketItemId,
    ITicketServiceItemPriceInformationBodyItem | null
  >
>({})

const ticketItemSelections = computed<
  ITicketServiceItemPriceInformationBodyItem[]
>(() => {
  return Object.values(ticketItemSelectionsRecords.value).filter(
    (value) => value !== null,
  )
})

const anySelected = computed<boolean>(() => {
  return ticketItemSelections.value.length > 0
})

const alerts = computed<Alert[]>(() => {
  const alertList: Alert[] = []
  const nonFieldErrors = (errors.value?.non_field_errors || []) as Array<string>
  for (const errorMessage of nonFieldErrors) {
    alertList.push({
      text: errorMessage,
      alertType: ALERT_TYPES.DANGER,
    })
  }
  return alertList
})

const payload = computed<ICreateTicketServiceOrderItem>(() => {
  return {
    service: props.service.id,
    items: ticketItemSelections.value,
    referral_type: props.referral?.referral_type,
    referral_code: props.referral?.referral_code,
    item_group: null,
  }
})

const { mutateAsync: fetchAvailability } =
  useFetchTicketItemDateAvailabilityListMutation()

// prefer debouncedOnFetchAvailability usage
const onFetchAvailability = () => {
  if (selectedDate.value === null) {
    availableTicketItems.value = null
    return
  }
  fetchAvailability({
    hashId: props.service.hash_id,
    body: {
      date: formatServerDate(selectedDate.value),
    },
  })
    .then((data: ITicketItemDateAvailability[]) => {
      availableTicketItems.value = data
      const availableTicketItemIds = availableTicketItems.value.map(
        (item) => item.ticket_item,
      )
      Object.keys(ticketItemSelectionsRecords.value).forEach((ticketItemId) => {
        if (!availableTicketItemIds.includes(ticketItemId)) {
          delete ticketItemSelectionsRecords.value[ticketItemId]
        }
      })
    })
    .catch((error) => {
      errors.value = getValidationErrorData(error)
    })
}

const debouncedOnFetchAvailability = debounce(onFetchAvailability, 400)

/**
 * Total base price without discounts. For final value use formattedTotalPrice
 */
const formattedBaseTotalPrice = computed<string>(() => {
  const baseTotal = priceInfo.value?.base_total
  return isNumeric(baseTotal) ? formatCurrency(baseTotal) : '- €'
})

/**
 * Overall total price with discounts included
 */
const formattedTotalPrice = computed<string>(() => {
  const discountedTotal = priceInfo.value?.discounted_total
  return isNumeric(discountedTotal) ? formatCurrency(discountedTotal) : '- €'
})

const selectedPricesText = computed<string>(() => {
  return '' // add content for group selection mode
})

const { mutateAsync: fetchPriceInfo } =
  useFetchTicketItemPriceInformationMutation()

const updatePriceInfo = () => {
  fetchPriceInfo({
    service: props.isPreview ? props.service.id : props.service.hash_id,
    isPreview: props.isPreview,
    body: {
      items: ticketItemSelections.value,
      order_item: null,
      group_service: props.groupService,
    },
  })
    .then((data) => {
      priceInfo.value = data
    })
    .catch((error) => {
      errors.value = getValidationErrorData(error)
    })
    .finally(() => {
      isLoadedOnce.value = true
    })
}

// Use this to get updates in controlled amount after group size changes.
const debouncedUpdatePriceInfo = debounce(updatePriceInfo, 400)

const { mutateAsync: createOrder } = useCreateOrderMutation()

const addItemToCart = async () => {
  isAddToCartLoading.value = true
  errors.value = undefined
  if (!orderStore.orderUuid) {
    try {
      const data = await createOrder()
      orderStore.updateOrder(data)
    } catch (error: unknown) {
      errors.value = getValidationErrorData(error)
      isAddToCartLoading.value = false
      return
    }
  }

  createTicketItemOrderItems(orderStore.orderUuid, payload.value)
    .then((response) => {
      orderStore.updateOrder(response.data)
      toast.success(i18n.t('addToCartSuccess'))
      emits('cartUpdated')
      ticketItemSelectionsRecords.value = {}
    })
    .catch((error) => {
      errors.value = getValidationErrorData(error)
    })
    .finally(() => {
      isAddToCartLoading.value = false
    })
}

const isDiscounted = computed<boolean>(() => {
  return priceInfo.value?.base_total !== priceInfo.value?.discounted_total
})

watch(
  selectedDate,
  () => {
    debouncedOnFetchAvailability()
  },
  {
    immediate: true,
  },
)

watch(
  ticketItemSelections,
  () => {
    debouncedUpdatePriceInfo()
  },
  {
    immediate: true,
  },
)

watch(
  payload,
  () => {
    emits('payloadUpdated', payload.value)
  },
  { immediate: true },
)



return (_ctx: any,_cache: any) => {
  const _component_BaseButton = _resolveComponent("BaseButton")!
  const _component_AlertBanner = _resolveComponent("AlertBanner")!

  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["ticket-card", { 'result-card--grouped': !!props.groupService }])
  }, [
    (!_ctx.showSelectedMode)
      ? (_openBlock(), _createBlock(TicketResultCardSearch, {
          key: 0,
          "selected-date": selectedDate.value,
          "onUpdate:selected-date": _cache[0] || (_cache[0] = ($event: any) => ((selectedDate).value = $event)),
          service: _ctx.service,
          "group-service": _ctx.groupService,
          class: "ticket-card__search"
        }, null, 8, ["selected-date", "service", "group-service"]))
      : _createCommentVNode("", true),
    (availableTicketItems.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          (!_ctx.showSelectedMode)
            ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                _createElementVNode("div", _hoisted_1, [
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(availableTicketItems.value, (item, itemIndex) => {
                    return (_openBlock(), _createBlock(TicketResultCardItem, {
                      key: item.ticket_item,
                      "ticket-item": item,
                      selection: ticketItemSelectionsRecords.value[item.ticket_item] ?? null,
                      "selected-group-size": __props.selectedGroupSize,
                      errors: errors.value?.items?.[itemIndex],
                      "onUpdate:selection": 
              (value) => (ticketItemSelectionsRecords.value[item.ticket_item] = value)
            
                    }, null, 8, ["ticket-item", "selection", "selected-group-size", "errors", "onUpdate:selection"]))
                  }), 128))
                ]),
                _hoisted_2
              ], 64))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, [
              _createElementVNode("div", _hoisted_5, [
                (!_ctx.showSelectedMode)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_6, _toDisplayString(_ctx.$t('totalPrice')), 1))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_7, [
                  (_unref(isDiscounted))
                    ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(_unref(formattedBaseTotalPrice)), 1))
                    : _createCommentVNode("", true),
                  _createElementVNode("span", {
                    class: _normalizeClass(["ticket-card__price", [_unref(isDiscounted) && 'ticket-card__price--discounted']])
                  }, _toDisplayString(_unref(formattedTotalPrice)), 3)
                ])
              ]),
              (_ctx.showSelectedMode)
                ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(_unref(selectedPricesText)), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_10, [
              _renderSlot(_ctx.$slots, "addButton", {
                payload: _unref(payload),
                contentSelected: _unref(anySelected)
              }, () => [
                _createVNode(_component_BaseButton, {
                  format: _unref(FORMATS).PRIMARY,
                  size: _unref(SIZES).SMALL,
                  label: _ctx.$t('addToCart'),
                  loading: isAddToCartLoading.value,
                  class: "ticket-card__action",
                  icon: "shopping-cart",
                  disabled: _ctx.isPreview || !_ctx.service.id || !_unref(anySelected),
                  "icon-right": "",
                  onClick: _withModifiers(addItemToCart, ["prevent"])
                }, null, 8, ["format", "size", "label", "loading", "disabled", "onClick"])
              ])
            ])
          ]),
          (_unref(alerts).length)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(alerts), (alert, alertIndex) => {
                  return (_openBlock(), _createBlock(_component_AlertBanner, _mergeProps({ key: alertIndex }, alert, { class: "ticket-card__alert" }), null, 16))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ], 64))
      : _createCommentVNode("", true)
  ], 2))
}
}

})